import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUserShield, FaUserPlus, FaUserMinus, FaCheckCircle } from 'react-icons/fa';
import { setAdminRole } from '../utils/setAdminRole';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  padding: 30px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: #546140;
  }
`;

const AdminForm = styled.form`
  margin-bottom: 40px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #546140;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 12px 20px;
  background-color: ${props => props.variant === 'danger' ? '#e74c3c' : '#546140'};
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${props => props.variant === 'danger' ? '#c0392b' : '#3e472f'};
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AdminInfo = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-top: 30px;
`;

const InfoHeader = styled.h3`
  font-size: 18px;
  margin-bottom: 15px;
  color: #333;
`;

const InfoText = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 10px;
`;

const Notification = styled.div`
  padding: 15px;
  margin: 20px 0;
  border-radius: 4px;
  background-color: ${props => props.type === 'success' ? '#edf7ed' : '#fff5f5'};
  color: ${props => props.type === 'success' ? '#2e7d32' : '#d32f2f'};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const StatusCard = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UserStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  span {
    font-weight: 500;
  }
  
  .user-id {
    color: #666;
    font-size: 14px;
    margin-top: 5px;
  }
`;

const StatusBadge = styled.div`
  padding: 6px 12px;
  background-color: ${props => props.isAdmin ? '#edf7ed' : '#fff5f5'};
  color: ${props => props.isAdmin ? '#2e7d32' : '#d32f2f'};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
`;

const AdminManagement = () => {
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const { currentUser, userRoles } = useAuth();
  
  // Check the current user's admin status on load
  useEffect(() => {
    if (currentUser) {
      setUserStatus({
        userId: currentUser.uid,
        email: currentUser.email,
        isAdmin: userRoles?.admin === true
      });
    }
  }, [currentUser, userRoles]);
  
  const handleGrantAdmin = async (e) => {
    e.preventDefault();
    
    if (!userId.trim()) {
      setNotification({
        type: 'error',
        message: 'Please enter a valid user ID'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const success = await setAdminRole(userId, true);
      
      if (success) {
        setNotification({
          type: 'success',
          message: `Admin access granted to user: ${userId}`
        });
        setUserId('');
      } else {
        setNotification({
          type: 'error',
          message: 'Failed to grant admin access. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error granting admin access:', error);
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleRevokeAdmin = async (e) => {
    e.preventDefault();
    
    if (!userId.trim()) {
      setNotification({
        type: 'error',
        message: 'Please enter a valid user ID'
      });
      return;
    }
    
    if (!window.confirm(`Are you sure you want to revoke admin access from ${userId}?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      const success = await setAdminRole(userId, false);
      
      if (success) {
        setNotification({
          type: 'success',
          message: `Admin access revoked from user: ${userId}`
        });
        setUserId('');
      } else {
        setNotification({
          type: 'error',
          message: 'Failed to revoke admin access. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error revoking admin access:', error);
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Title>
        <FaUserShield /> Admin Management
      </Title>
      
      {userStatus && (
        <StatusCard>
          <UserStatus>
            <div>
              <span>Current User: {userStatus.email}</span>
              <div className="user-id">ID: {userStatus.userId}</div>
            </div>
          </UserStatus>
          <StatusBadge isAdmin={userStatus.isAdmin}>
            {userStatus.isAdmin ? 'Admin Access' : 'Not Admin'}
          </StatusBadge>
        </StatusCard>
      )}
      
      {notification && (
        <Notification type={notification.type}>
          {notification.type === 'success' ? <FaCheckCircle /> : null}
          {notification.message}
        </Notification>
      )}
      
      <AdminForm>
        <InputGroup>
          <Label htmlFor="userId">Firebase User ID</Label>
          <Input 
            type="text" 
            id="userId" 
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Enter the Firebase user ID"
          />
        </InputGroup>
        
        <ButtonRow>
          <Button 
            onClick={handleGrantAdmin}
            disabled={loading || !userId.trim()}
          >
            <FaUserPlus /> Grant Admin Access
          </Button>
          
          <Button 
            variant="danger"
            onClick={handleRevokeAdmin}
            disabled={loading || !userId.trim()}
          >
            <FaUserMinus /> Revoke Admin Access
          </Button>
        </ButtonRow>
      </AdminForm>
      
      <AdminInfo>
        <InfoHeader>How to find a user ID</InfoHeader>
        <InfoText>
          User IDs can be found in the Firebase Authentication console. 
          Go to the Firebase console, navigate to Authentication, and click on the Users tab.
          Each user's ID is listed in the User UID column.
        </InfoText>
        <InfoText>
          Note: Only grant admin access to trusted users who require administrative capabilities.
        </InfoText>
      </AdminInfo>
    </Container>
  );
};

export default AdminManagement; 